export const SLOT_HEIGHT_PX = 24;
export const DEFAULT_START_TIME = '09:00';
export const SLOTS_PER_HOUR = 2;
export const CARD_CAPTION_HEIGHT = 56;
export const PLACE_CARD_CAPTION_HEIGHT = 72;
export const CONNECTOR_ICON_BOTTOM_PX = 18;
export const STACK_OFFSET_PX = 10;
export const LANE_OFFSET_PX = 4;
export const PLACE_CARD_RESIZE_HEIGHT = 0;
export const PLACE_CARD_MIN_CONTENT_HEIGHT = 56 + PLACE_CARD_CAPTION_HEIGHT;

/** Parse "HH:MM" to [hours, minutes]. Returns [0, 0] for malformed input. */
export function parseTime(time: string): [number, number] {
  const [h, m] = time.split(':').map(Number);
  return [Number.isFinite(h) ? h! : 0, Number.isFinite(m) ? m! : 0];
}

/** Parse "HH:MM" to minutes-since-midnight. Returns 0 for malformed input. */
export function timeToMinutes(time: string): number {
  const [h, m] = parseTime(time);
  return h * 60 + m;
}

/**
 * Sort items in-place by `startTime` ascending. Items with no startTime sort
 * as if they were at DEFAULT_START_TIME. Returns the same (now sorted) array.
 */
export function sortByStartTime<T extends { startTime?: string | undefined }>(items: T[]): T[] {
  return items.sort(
    (a, b) =>
      timeToMinutes(a.startTime ?? DEFAULT_START_TIME) -
      timeToMinutes(b.startTime ?? DEFAULT_START_TIME),
  );
}

export function addMinutes(time: string, minutes: number): string {
  const [h, m] = parseTime(time || '00:00');
  const total = Math.min(h * 60 + m + minutes, 1440);
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

export function endTimeForSlots(endTime: string): string {
  return endTime === '00:00' ? '24:00' : endTime;
}

export function minutesBetween(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  let [eh, em] = endTime.split(':').map(Number);
  const startMins = (sh ?? 0) * 60 + (sm ?? 0);
  if (endTime === '00:00' && startMins > 0) { eh = 24; em = 0; }
  return (eh ?? 0) * 60 + (em ?? 0) - startMins;
}

export function getEndTime(item: { startTime?: string | undefined; endTime?: string | undefined }, defaultMinutes = 60): string {
  return item.endTime ?? addMinutes(item.startTime ?? DEFAULT_START_TIME, defaultMinutes);
}

export function getPlaceCardOverflow(placeSlotHeight: number): number {
  if (placeSlotHeight >= PLACE_CARD_MIN_CONTENT_HEIGHT) return 0;
  return Math.max(0, PLACE_CARD_MIN_CONTENT_HEIGHT - placeSlotHeight);
}

export function getSlotCount(minHeight: number): number {
  return Math.max(1, Math.round(minHeight / SLOT_HEIGHT_PX));
}

export function shouldShowImageArea(slotCount: number): boolean {
  return slotCount >= 3;
}

export function getImageAreaMinHeight(minHeight: number, captionHeight: number = CARD_CAPTION_HEIGHT): number {
  return Math.max(56, minHeight - captionHeight);
}

export function parseDayHours(hours?: string | undefined): { start: number; end: number } {
  if (!hours) return { start: 0, end: 24 };
  const parts = hours.match(/(\d{1,2})[^\d]*(\d{1,2})/);
  if (!parts) return { start: 0, end: 24 };
  return { start: parseInt(parts[1]!, 10), end: parseInt(parts[2]!, 10) };
}

function to12h(h24: number): number {
  return h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
}

export function buildTimeSlots(startHour: number, endHour: number): { label: string; startTime: string }[] {
  const slots: { label: string; startTime: string }[] = [];
  for (let h = startHour; h <= endHour; h++) {
    slots.push({ label: `${to12h(h)}:00`, startTime: `${String(h).padStart(2, '0')}:00` });
    if (h < endHour) {
      slots.push({ label: `${to12h(h)}:30`, startTime: `${String(h).padStart(2, '0')}:30` });
    }
  }
  return slots;
}
